import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  ExternalLink,
  FolderGit2,
  GitPullRequest,
  ListTodo,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CreateProjectDialog } from "@/components/create-project-dialog";
import { DeleteProjectDialog } from "@/components/delete-project-dialog";
import { EditProjectDialog } from "@/components/edit-project-dialog";
import { cn } from "@/lib/cn";
import {
  listOrganizations,
  listProjectPullRequests,
  listProjectTasks,
  listProjects,
  type Project,
} from "@/lib/console-api";
import { useWorkspaceStore } from "@/stores/workspace-store";

const TASK_STATUS_LABEL: Record<string, string> = {
  TODO: "대기",
  IN_PROGRESS: "진행 중",
  REVIEW: "리뷰",
  REVISION: "수정",
  DONE: "완료",
  FAILED: "실패",
  CANCELLED: "취소",
};

const PR_STATUS_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  OPEN: "Open",
  APPROVED: "Approved",
  MERGED: "Merged",
  CLOSED: "Closed",
};

const STATUS_COLOR: Record<string, string> = {
  TODO: "bg-muted text-muted-foreground",
  IN_PROGRESS: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  REVIEW: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  REVISION: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  DONE: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  FAILED: "bg-destructive/10 text-destructive",
  CANCELLED: "bg-muted text-muted-foreground",
  DRAFT: "bg-muted text-muted-foreground",
  OPEN: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  APPROVED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  MERGED: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  CLOSED: "bg-muted text-muted-foreground",
};

export function ProjectsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const selectedOrgId = useWorkspaceStore((s) => s.selectedOrgId);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: listOrganizations,
  });
  const projectsQuery = useQuery({
    // 선택된 워크스페이스의 프로젝트만 fetch — 서버 측 필터링.
    queryKey: ["projects", { organizationId: selectedOrgId }],
    queryFn: () => listProjects(selectedOrgId ?? undefined),
    enabled: !!selectedOrgId,
  });

  const orgs = orgsQuery.data ?? [];
  const projects = projectsQuery.data?.items ?? [];

  // 조직별로 그룹핑.
  const grouped = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of projects) {
      const list = map.get(p.organizationId) ?? [];
      list.push(p);
      map.set(p.organizationId, list);
    }
    return map;
  }, [projects]);

  const orgName = (orgId: string): string =>
    orgs.find((o) => o.id === orgId)?.name ?? orgId;

  // 프로젝트가 속한 조직에서의 본인 역할 — 삭제 (OWNER/ADMIN) 가능 여부 판단.
  const myRoleIn = (orgId: string): string | null =>
    orgs.find((o) => o.id === orgId)?.role ?? null;
  const canDelete = (project: Project): boolean => {
    const role = myRoleIn(project.organizationId);
    return role === "OWNER" || role === "ADMIN";
  };
  // 수정은 MEMBER 이상이면 가능 (cloud-api 의 MUTATE_ROLES 와 일치).
  const canEdit = (project: Project): boolean => {
    const role = myRoleIn(project.organizationId);
    return role === "OWNER" || role === "ADMIN" || role === "MEMBER";
  };

  const invalidateProjects = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["projects"] });
  };

  const isLoading = orgsQuery.isLoading || projectsQuery.isLoading;

  return (
    <div className="space-y-6 p-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">프로젝트</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">프로젝트</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            조직에 등록된 프로젝트를 관리하세요. 데스크톱 앱에서 이 프로젝트를 골라 로컬 폴더에 연결하면 작업이 시작됩니다.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={orgs.length === 0}
        >
          <Plus className="h-4 w-4" />새 프로젝트
        </Button>
      </header>

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {!isLoading && projects.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <FolderGit2 className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="mt-3 font-medium">등록된 프로젝트가 없습니다</p>
          <p className="mt-1 text-sm text-muted-foreground">
            위의 "새 프로젝트" 버튼으로 등록하거나, 데스크톱 앱의 온보딩에서 만들 수 있어요.
          </p>
          <Button
            type="button"
            className="mt-5"
            onClick={() => setCreateOpen(true)}
            disabled={orgs.length === 0}
          >
            <Plus className="h-4 w-4" />새 프로젝트
          </Button>
        </div>
      )}

      {!isLoading && projects.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr,3fr]">
          {/* 좌: 조직별 그룹 + 프로젝트 */}
          <div className="space-y-5">
            {Array.from(grouped.entries()).map(([orgId, list]) => (
              <div key={orgId}>
                <h2 className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {orgName(orgId)}
                </h2>
                <ul className="space-y-2">
                  {list.map((p) => {
                    const isActive = activeProject?.id === p.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => setActiveProject(p)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors",
                            isActive
                              ? "border-primary bg-primary/5"
                              : "border-border bg-card hover:bg-accent/50",
                          )}
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{p.name}</p>
                            <p className="mt-1 truncate text-xs text-muted-foreground">
                              {p.gitUrl}
                            </p>
                          </div>
                          <ChevronRight
                            className={cn(
                              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                              isActive && "rotate-90 text-foreground",
                            )}
                            aria-hidden
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* 우: 선택 프로젝트 상세 */}
          <div className="rounded-lg border border-border bg-card p-6">
            {activeProject ? (
              <ProjectDetail
                project={activeProject}
                canEdit={canEdit(activeProject)}
                canDelete={canDelete(activeProject)}
                onEdit={() => setEditOpen(true)}
                onDelete={() => setDeleteOpen(true)}
              />
            ) : (
              <div className="flex h-full min-h-[20rem] items-center justify-center text-center text-sm text-muted-foreground">
                왼쪽에서 프로젝트를 선택하면 태스크와 PR 을 볼 수 있어요
              </div>
            )}
          </div>
        </div>
      )}

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => {
          invalidateProjects();
          setActiveProject(p);
        }}
        organizations={orgs}
        defaultOrganizationId={selectedOrgId ?? orgs[0]?.id}
      />
      {activeProject && (
        <>
          <EditProjectDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            onUpdated={(p) => {
              invalidateProjects();
              setActiveProject(p);
            }}
            project={activeProject}
          />
          <DeleteProjectDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onDeleted={() => {
              invalidateProjects();
              setActiveProject(null);
            }}
            project={activeProject}
          />
        </>
      )}
    </div>
  );
}

function ProjectDetail({
  project,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  project: Project;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const tasksQuery = useQuery({
    queryKey: ["project-tasks", project.id],
    queryFn: () => listProjectTasks(project.id, { pageSize: 10 }),
  });
  const prsQuery = useQuery({
    queryKey: ["project-prs", project.id],
    queryFn: () => listProjectPullRequests(project.id, { pageSize: 10 }),
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{project.name}</h2>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {project.gitUrl || "(Git URL 미설정)"} · {project.defaultBranch}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            <span className="font-mono">{project.slug}</span> · {project.commitStrategy}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onEdit}
              aria-label="프로젝트 수정"
            >
              <Pencil className="h-3.5 w-3.5" />
              수정
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDelete}
              aria-label="프로젝트 삭제"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              삭제
            </Button>
          )}
        </div>
      </div>

      <section className="mt-6">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <ListTodo className="h-4 w-4" aria-hidden />
          태스크 {tasksQuery.data?.total ?? 0}개{" "}
          <span className="text-xs font-normal text-muted-foreground">
            (최근 10개)
          </span>
        </div>
        {tasksQuery.isLoading && (
          <p className="text-sm text-muted-foreground">불러오는 중…</p>
        )}
        {!tasksQuery.isLoading &&
          (tasksQuery.data?.items.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">태스크가 없습니다.</p>
          )}
        {!tasksQuery.isLoading &&
          (tasksQuery.data?.items.length ?? 0) > 0 && (
            <ul className="divide-y divide-border rounded-md border border-border">
              {tasksQuery.data!.items.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-muted-foreground">
                      {t.type}
                    </span>
                    <p className="truncate" title={t.title}>
                      {t.title}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded px-2 py-0.5 text-xs",
                      STATUS_COLOR[t.status] ?? "bg-muted text-muted-foreground",
                    )}
                  >
                    {TASK_STATUS_LABEL[t.status] ?? t.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </section>

      <section className="mt-6">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <GitPullRequest className="h-4 w-4" aria-hidden />
          Pull Request {prsQuery.data?.total ?? 0}개{" "}
          <span className="text-xs font-normal text-muted-foreground">
            (최근 10개)
          </span>
        </div>
        {prsQuery.isLoading && (
          <p className="text-sm text-muted-foreground">불러오는 중…</p>
        )}
        {!prsQuery.isLoading && (prsQuery.data?.items.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground">PR 이 없습니다.</p>
        )}
        {!prsQuery.isLoading && (prsQuery.data?.items.length ?? 0) > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border">
            {prsQuery.data!.items.map((pr) => (
              <li
                key={pr.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <a
                  href={pr.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-w-0 items-center gap-2 hover:underline"
                >
                  <span className="font-mono text-xs text-muted-foreground">
                    #{pr.providerPrId}
                  </span>
                  <p className="truncate" title={pr.title}>
                    {pr.title}
                  </p>
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </a>
                <span
                  className={cn(
                    "shrink-0 rounded px-2 py-0.5 text-xs",
                    STATUS_COLOR[pr.status] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {PR_STATUS_LABEL[pr.status] ?? pr.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
